import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getPlatform } from "@/lib/platform";
import { toast } from "sonner";

/**
 * On login, checks for unseen admin toast notifications matching the current platform.
 * Shows each one as a toast and marks it as seen so it never repeats.
 */
export const useLoginToastNotifications = (userId: string | null) => {
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!userId || hasChecked.current) return;
    hasChecked.current = true;

    const checkNotifications = async () => {
      try {
        const platform = getPlatform(); // 'ios' | 'android' | 'web'

        // Get all toast notifications targeting this platform
        const { data: allNotifs, error: notifError } = await supabase
          .from("admin_toast_notifications")
          .select("id, message, target_platforms, created_at")
          .contains("target_platforms", [platform])
          .order("created_at", { ascending: false });

        if (notifError || !allNotifs?.length) return;

        // Get already-seen notification IDs for this user
        const { data: seenRecords } = await supabase
          .from("admin_toast_seen")
          .select("notification_id")
          .eq("user_id", userId);

        const seenIds = new Set((seenRecords || []).map((r: any) => r.notification_id));
        const unseen = (allNotifs as any[]).filter((n) => !seenIds.has(n.id));

        if (!unseen.length) return;

        // Show each unseen notification as a toast with a delay
        for (let i = 0; i < unseen.length; i++) {
          const notification = unseen[i];

          setTimeout(() => {
            toast.info(notification.message, { duration: 6000 });
          }, i * 2000);

          // Mark as seen immediately
          await supabase.from("admin_toast_seen").insert({
            notification_id: notification.id,
            user_id: userId,
          } as any);
        }
      } catch (error) {
        console.error("Failed to check login toast notifications:", error);
      }
    };

    checkNotifications();
  }, [userId]);
};
